/* ============================================================
   DepartmentSetup — a verified Department Manager signup picks the departments
   they manage. Shown after email confirmation, or at sign-in while approval is
   pending and nothing was picked yet. Authorised by a short-lived setup_token,
   so the department list is never public. Picked once; an admin can change it.
   ============================================================ */

import { useEffect, useState } from 'react'
import Alert from './Alert'
import Spinner from './Spinner'
import DepartmentPicker from './DepartmentPicker'
import { saveSignupDepartments, signupDepartmentOptions } from '../api/auth'
import { authErrorMessage } from '../lib/errors'

export default function DepartmentSetup({ setupToken }) {
  const [departments, setDepartments] = useState(null)
  const [picked, setPicked] = useState([])
  const [error, setError] = useState('')
  const [alert, setAlert] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    signupDepartmentOptions(setupToken)
      .then((res) => setDepartments(res?.data?.departments ?? []))
      .catch((err) => setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not load departments.') }))
  }, [setupToken])

  async function onSave(e) {
    e.preventDefault()
    if (!picked.length) {
      setError('Choose the departments you manage')
      return
    }
    setError('')
    setAlert(null)
    setBusy(true)
    try {
      await saveSignupDepartments(setupToken, picked)
      setDone(true)
      setAlert({ type: 'success', message: 'Saved. An admin will review your request and approve your account.' })
    } catch (err) {
      setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not save your departments.') })
    } finally {
      setBusy(false)
    }
  }

  if (done) return <Alert type={alert?.type}>{alert?.message}</Alert>

  return (
    <form onSubmit={onSave} noValidate>
      <Alert type={alert?.type}>{alert?.message}</Alert>
      {departments === null ? (
        !alert && (
          <p className="countdown" aria-live="polite">
            <Spinner label="Loading departments" /> Loading departments…
          </p>
        )
      ) : (
        <>
          <DepartmentPicker
            departments={departments}
            value={picked}
            onChange={setPicked}
            disabled={busy}
            error={error}
            label="Departments you manage"
          />
          <p className="su-note">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
            An admin reviews this before your account is approved. If you skip it now, you’ll be asked when you sign in.
          </p>
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner label="Saving" /> : null}
            {busy ? 'Saving…' : 'Save departments'}
          </button>
        </>
      )}
    </form>
  )
}
